import React, { useEffect, useState } from 'react';
import { Download, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { staffMasterApi, TEMPLATE_HEADERS } from '@/services/staffMasterApi';

export const StaffImportDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [prog, setProg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setProg(null); } }, [open]);

  const run = async () => {
    if (!file) { toast.error('Choose a CSV file'); return; }
    setBusy(true);
    try {
      const r = await staffMasterApi.importCsv(file, setProg);
      setProg(r);
      toast.success(`Imported ${r?.imported ?? 0} staff (${r?.skipped ?? 0} skipped)`);
      onDone();
    } catch (e) { toast.error(e.message); setProg({ error: e.message }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="staff-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Staff CSV</DialogTitle>
          <DialogDescription>Existing Staff IDs are updated, new ones are created. Blank Gender / Worker Type / Collar / Pay Type / Fixed Pay / Roll cells keep the current value.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 border p-3 text-xs text-gray-600 space-y-1">
            <p className="font-semibold text-gray-800">Columns</p>
            <p className="font-mono leading-relaxed" data-testid="staff-import-columns">{TEMPLATE_HEADERS.join(', ')}</p>
            <p>roll_status: <b>In Roll</b> or <b>Left</b>. If left_date is blank for a Left worker, today is used.</p>
            <p>pay_type: <b>Fixed</b> or <b>Variable</b> (regular staff). fixed_pay = monthly amount, needed when pay_type is Fixed (paid ÷ 26 per present day).</p>
          </div>
          <Button variant="outline" size="sm" onClick={staffMasterApi.downloadTemplate} data-testid="staff-import-template"><Download className="w-4 h-4 mr-1" />Download template</Button>
          <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setProg(null); }}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#2D5F4F] file:px-3 file:py-2 file:text-white" data-testid="staff-import-file" />
          {prog && !prog.error && (
            <div className="space-y-1" data-testid="staff-import-progress">
              <Progress value={prog.percentage || 0} />
              <p className="text-xs text-gray-600">{prog.processed ?? 0}/{prog.total ?? 0} rows · {prog.imported ?? 0} imported · {prog.skipped ?? 0} skipped</p>
              {(prog.errors || []).map((er) => <p key={er} className="text-xs text-red-600">{er}</p>)}
            </div>
          )}
          {prog?.error && <p className="text-sm text-red-600" data-testid="staff-import-error">{prog.error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="staff-import-close">Close</Button>
          <Button onClick={run} disabled={busy || !file} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="staff-import-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StaffImportDialog;
